import type { AnimalItem, ShareItem } from "../mocks/animals";
import type { CustomerItem } from "../mocks/customers";
import type { PaymentRecord } from "../mocks/payments";
import type { DeliveryAssignment } from "./deliveryStore";

function normalizePhone(value?: string) {
  return (value ?? "").replace(/\s/g, "");
}

function normalizeName(value?: string) {
  return (value ?? "").trim().toLowerCase();
}

export function isShareOwnedByCustomer(share: ShareItem, customer: CustomerItem) {
  const sharePhone = normalizePhone(share.telefon);
  const customerPhone = normalizePhone(customer.telefon);

  if (sharePhone && customerPhone && sharePhone === customerPhone) {
    return true;
  }

  return normalizeName(share.musteriAdi) === normalizeName(customer.adSoyad);
}

export function getShareTotal(share: ShareItem) {
  return (
    share.tutar +
    (share.kesimUcreti ?? 0) +
    (share.parcalamaUcreti ?? 0) +
    (share.tasimaUcreti ?? 0) -
    (share.indirim ?? 0)
  );
}

export function recalcShare(share: ShareItem): ShareItem {
  const total = getShareTotal(share);
  const safeOdenen = Math.max(0, share.odenen);
  const kalan = total - safeOdenen;

  const durum: ShareItem["durum"] =
    kalan <= 0 ? "Tam" : safeOdenen > 0 ? "Kısmi" : "Bekliyor";

  return {
    ...share,
    odenen: safeOdenen,
    kalan,
    durum,
  };
}

export function recalcAnimalTotals(animal: AnimalItem): AnimalItem {
  const hisseler = animal.hisseler
    .map(recalcShare)
    .sort((a, b) => {
      const aNo = getSlotNoFromHisseNo(a.hisseNo);
      const bNo = getSlotNoFromHisseNo(b.hisseNo);
      return aNo - bNo;
    });

  return {
    ...animal,
    hisseler,
    satirlanHisse: hisseler.reduce((sum, share) => sum + share.hisseSayisi, 0),
    toplamSatisTutari: hisseler.reduce((sum, share) => sum + getShareTotal(share), 0),
    toplamOdenen: hisseler.reduce((sum, share) => sum + share.odenen, 0),
  };
}

export function recalcAllAnimals(animals: AnimalItem[]) {
  return animals.map(recalcAnimalTotals);
}

export function recalcCustomersFromAnimals(
  customers: CustomerItem[],
  animals: AnimalItem[]
): CustomerItem[] {
  return customers.map((customer) => {
    const linkedShares = animals.flatMap((animal) =>
      animal.hisseler.filter((share) => isShareOwnedByCustomer(share, customer))
    );

    return {
      ...customer,
      toplamHisseSayisi: linkedShares.reduce((sum, share) => sum + share.hisseSayisi, 0),
      toplamBorc: linkedShares.reduce((sum, share) => sum + getShareTotal(share), 0),
      toplamOdenen: linkedShares.reduce((sum, share) => sum + share.odenen, 0),
    };
  });
}

export function getSlotNoFromHisseNo(hisseNo: string) {
  const parts = hisseNo.split("-");
  const last = parseInt(parts[parts.length - 1], 10);
  return Number.isNaN(last) ? 0 : last;
}

export function analyzeCustomerDeletion(
  customerId: number,
  customers: CustomerItem[],
  animals: AnimalItem[],
  payments: PaymentRecord[]
) {
  const customer = customers.find((c) => c.id === customerId);
  if (!customer) {
    return {
      customer: null,
      shareCount: 0,
      paymentCount: 0,
      refundTotal: 0,
    };
  }

  const linkedShares = animals.flatMap((animal) =>
    animal.hisseler.filter((share) => isShareOwnedByCustomer(share, customer))
  );

  const shareIds = new Set(linkedShares.map((share) => share.id));

  const linkedPayments = payments.filter(
    (payment) =>
      payment.musteriId === customer.id ||
      shareIds.has(payment.shareId) ||
      normalizePhone(payment.telefon) === normalizePhone(customer.telefon) ||
      normalizeName(payment.musteriAdi) === normalizeName(customer.adSoyad)
  );

  return {
    customer,
    shareCount: linkedShares.length,
    paymentCount: linkedPayments.length,
    refundTotal: linkedShares.reduce((sum, share) => sum + share.odenen, 0),
  };
}

export function analyzeAnimalDeletion(
  animalId: number,
  animals: AnimalItem[],
  payments: PaymentRecord[],
  customers: CustomerItem[]
) {
  const animal = animals.find((a) => a.id === animalId);
  if (!animal) {
    return {
      animal: null,
      shareCount: 0,
      paymentCount: 0,
      affectedCustomerCount: 0,
    };
  }

  const shareIds = new Set(animal.hisseler.map((share) => share.id));

  const linkedPayments = payments.filter(
    (payment) => payment.animalId === animal.id || shareIds.has(payment.shareId)
  );

  const affectedCustomers = customers.filter((customer) =>
    animal.hisseler.some((share) => isShareOwnedByCustomer(share, customer))
  );

  return {
    animal,
    shareCount: animal.hisseler.length,
    paymentCount: linkedPayments.length,
    affectedCustomerCount: affectedCustomers.length,
  };
}

export function deletePaymentCascade(params: {
  paymentId: number;
  animals: AnimalItem[];
  customers: CustomerItem[];
  payments: PaymentRecord[];
}) {
  const { paymentId, animals, customers, payments } = params;

  const targetPayment = payments.find((payment) => payment.id === paymentId);
  if (!targetPayment) {
    return {
      animals,
      customers,
      payments,
      deletedPayment: null,
    };
  }

  const nextAnimals = recalcAllAnimals(
    animals.map((animal) => {
      const hasTarget = animal.hisseler.some((share) => share.id === targetPayment.shareId);
      if (!hasTarget) return animal;

      return {
        ...animal,
        hisseler: animal.hisseler.map((share) => {
          if (share.id !== targetPayment.shareId) return share;

          return {
            ...share,
            odenen: Math.max(0, share.odenen - targetPayment.tutar),
          };
        }),
      };
    })
  );

  const nextPayments = payments.filter((payment) => payment.id !== paymentId);
  const nextCustomers = recalcCustomersFromAnimals(customers, nextAnimals);

  return {
    animals: nextAnimals,
    customers: nextCustomers,
    payments: nextPayments,
    deletedPayment: targetPayment,
  };
}

export function deleteCustomerCascade(params: {
  customerId: number;
  animals: AnimalItem[];
  customers: CustomerItem[];
  payments: PaymentRecord[];
}) {
  const { customerId, animals, customers, payments } = params;

  const customer = customers.find((c) => c.id === customerId);
  if (!customer) {
    return {
      animals,
      customers,
      payments,
      deletedCustomer: null,
    };
  }

  const removedShareIds = new Set<number>();

  const nextAnimals = recalcAllAnimals(
    animals.map((animal) => ({
      ...animal,
      hisseler: animal.hisseler.filter((share) => {
        const shouldRemove = isShareOwnedByCustomer(share, customer);
        if (shouldRemove) {
          removedShareIds.add(share.id);
        }
        return !shouldRemove;
      }),
    }))
  );

  const nextPayments = payments.filter((payment) => {
    const sameCustomerId = payment.musteriId === customer.id;
    const sameShare = removedShareIds.has(payment.shareId);
    const samePhone =
      normalizePhone(payment.telefon) === normalizePhone(customer.telefon);
    const sameName =
      normalizeName(payment.musteriAdi) === normalizeName(customer.adSoyad);

    return !(sameCustomerId || sameShare || samePhone || sameName);
  });

  const remainingCustomers = customers.filter((c) => c.id !== customerId);
  const nextCustomers = recalcCustomersFromAnimals(remainingCustomers, nextAnimals);

  return {
    animals: nextAnimals,
    customers: nextCustomers,
    payments: nextPayments,
    deletedCustomer: customer,
  };
}

export function deleteAnimalCascade(params: {
  animalId: number;
  animals: AnimalItem[];
  customers: CustomerItem[];
  payments: PaymentRecord[];
}) {
  const { animalId, animals, customers, payments } = params;

  const animal = animals.find((a) => a.id === animalId);
  if (!animal) {
    return {
      animals,
      customers,
      payments,
      deletedAnimal: null,
    };
  }

  const removedShareIds = new Set(animal.hisseler.map((share) => share.id));

  const nextAnimals = recalcAllAnimals(
    animals.filter((a) => a.id !== animalId)
  );

  const nextPayments = payments.filter(
    (payment) =>
      payment.animalId !== animalId && !removedShareIds.has(payment.shareId)
  );

  const nextCustomers = recalcCustomersFromAnimals(customers, nextAnimals);

  return {
    animals: nextAnimals,
    customers: nextCustomers,
    payments: nextPayments,
    deletedAnimal: animal,
  };
}

export function analyzeShareSaleDeletion(params: {
  shareId: number;
  animals: AnimalItem[];
  payments: PaymentRecord[];
}) {
  const { shareId, animals, payments } = params;

  for (const animal of animals) {
    const share = animal.hisseler.find((item) => item.id === shareId);
    if (!share) continue;

    const linkedPayments = payments.filter((payment) => payment.shareId === shareId);

    return {
      animal,
      share,
      paymentCount: linkedPayments.length,
      paymentTotal: linkedPayments.reduce((sum, payment) => sum + payment.tutar, 0),
    };
  }

  return {
    animal: null,
    share: null,
    paymentCount: 0,
    paymentTotal: 0,
  };
}

export function deleteShareSaleCascade(params: {
  shareId: number;
  animals: AnimalItem[];
  customers: CustomerItem[];
  payments: PaymentRecord[];
  deliveries?: DeliveryAssignment[];
}) {
  const { shareId, animals, customers, payments, deliveries = [] } = params;

  const analysis = analyzeShareSaleDeletion({
    shareId,
    animals,
    payments,
  });

  if (!analysis.share || !analysis.animal) {
    return {
      animals,
      customers,
      payments,
      deliveries,
      deletedAnimal: null,
      deletedShare: null,
    };
  }

  const nextAnimals = recalcAllAnimals(
    animals.map((animal) => {
      if (animal.id !== analysis.animal.id) return animal;

      return {
        ...animal,
        hisseler: animal.hisseler.filter((share) => share.id !== shareId),
      };
    })
  );

  const nextPayments = payments.filter((payment) => payment.shareId !== shareId);
  const nextDeliveries = deliveries.filter((delivery) => delivery.shareId !== shareId);
  const nextCustomers = recalcCustomersFromAnimals(customers, nextAnimals);

  return {
    animals: nextAnimals,
    customers: nextCustomers,
    payments: nextPayments,
    deliveries: nextDeliveries,
    deletedAnimal: analysis.animal,
    deletedShare: analysis.share,
  };
}